import { Injectable } from '@angular/core';

import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { QuickLinks, RacingConfig, RacingPromos } from '@frontend/sports/common/client-config-data-access';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';
import { MarketingViewModel } from '@frontend/sports/types/components/marketing';
import { DeviceService } from '@frontend/vanilla/core';
import { Observable, forkJoin, map, shareReplay } from 'rxjs';

import { DayOfRaceModel, TodayRaceModel } from '../../horse-racing-shared/model/day-of-race.model';
import { GreyhoundRacingApiService } from '../../horse-racing-shared/services/greyhound-racing-api.service';
import { toQuickLinkItem } from '../../quick-links/quick-links.models';

export interface RaceModel {
    dayRaceModel: TodayRaceModel<WinGameFixture>;
    day: DayOfRaceDate;
}

@Injectable({
    providedIn: 'root',
})
export class GreyhoundService {
    constructor(
        private quickLinks: QuickLinks,
        private racingPromos: RacingPromos,
        private api: GreyhoundRacingApiService,
        private racingConfig: RacingConfig,
        private deviceService: DeviceService,
    ) {}

    get getQuickLinks$() {
        return this.quickLinks.whenReady.pipe(
            map(() => {
                return { quickLinkItem: this.quickLinks.links.map(toQuickLinkItem), layout: this.quickLinks.layout };
            }),
            shareReplay(1),
        );
    }

    get getPromo$(): Observable<MarketingViewModel[]> {
        return this.racingPromos.whenReady.pipe(
            map(() => {
                return this.filterPromoBanners(this.racingPromos.promos);
            }),
            shareReplay(1),
        );
    }

    get getAllMeetings$(): Observable<RaceModel[]> {
        return forkJoin([this.api.getDayOfRaceFixtures(DayOfRaceDate.Today), this.api.getDayOfRaceFixtures(DayOfRaceDate.Tomorrow)]).pipe(
            map(([dayOfRaceToday, dayOfRaceTomorrow]: [DayOfRaceModel | undefined, DayOfRaceModel | undefined]) => {
                return [
                    {
                        dayRaceModel: { fixtures: dayOfRaceToday?.fixtures || [], regions: dayOfRaceToday?.regions || [] },
                        day: DayOfRaceDate.Today,
                    },
                    {
                        dayRaceModel: { fixtures: dayOfRaceTomorrow?.fixtures || [], regions: dayOfRaceTomorrow?.regions || [] },
                        day: DayOfRaceDate.Tomorrow,
                    },
                ] as RaceModel[];
            }),
        );
    }

    private filterPromoBanners(data: MarketingViewModel[]): MarketingViewModel[] {
        if (this.deviceService.isMobile && data.length >= this.racingConfig.minPromosForMobile) {
            return data.filter((_, index) => index < this.racingConfig.maxPromosForMobile);
        } else if (!this.deviceService.isMobile && data.length >= this.racingConfig.minPromos) {
            return data.filter((_, index) => index < this.racingConfig.maxPromos);
        }

        return [];
    }
}
